import React, { useCallback, useEffect, useState } from "react";
import {
    Link,
    useBlocker,
    useLocation,
    useNavigate,
    useNavigationType,
    useParams
} from "react-router-dom";
import {
    Alert,
    Button,
    Col,
    Form,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { BackButton } from "../../simple/BackButton";
import {
    AssetBundle,
    BuildType,
    CancelBatchBuildData,
    CancelBatchBuildVariables,
    CreateBatchBuildData,
    CreateBatchBuildVariables,
    ExampleCommandData,
    Mutator,
    MutatorAssetbundlesQueryData,
    MutatorCreateData,
    MutatorQueryData,
    MutatorUpdateData,
    MutatorUpdateInput,
    MutatorUpdateVariables,
    MutatorVariables,
    RevisionConfigData,
    RevisionVariables,
    TempBuildVariables,
    TempLink,
    TempLinkVariables
} from "../../../models/types";
import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
    useSubscription
} from "@apollo/client";
import {
    GET_EXAMPLE_COMMAND,
    GET_RESULTS,
    GET_REVISION_CONFIG,
    GET_TEMP_LINK,
    MUTATOR_ASSETBUNDLES,
    MUTATOR_QUERY
} from "../../../graphql/queries";
import {
    CANCEL_BUILD,
    CREATE_BUILD,
    MUTATOR_CREATE_CONFIG,
    MUTATOR_UPDATE_CONFIG
} from "../../../graphql/mutations";
import { MutatorOptions } from "./MutatorOptions";
import { EditableJsonView } from "../../editable/EditableJsonView";
import {
    useLocalState,
    useMutatorState,
    useRevision
} from "../../../graphql/hooks";
import {
    checkBuildDataVersion,
    loadMutatorConfigLocal,
    saveMutatorConfigLocal,
    updateMutatorState,
    updateNotificationState
} from "../../../common/Helpers";
import {
    MUTATOR_BUILD_SUBSCRIPTION,
    MUTATOR_CONFIG_SUBSCRIPTION
} from "../../../graphql/subscriptions";
import { SimpleToast } from "../../simple/SimpleToast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronCircleLeft,
    faCircleInfo
} from "@fortawesome/free-solid-svg-icons";
import { fetchHtmlFromS3 } from "../../../common/MutatorHelpers";
import { AlertModal } from "../../modals/AlertModal";
import { BuildOverviewModal } from "../../modals/BuildOverviewModal";
import { EditPanelBuilds } from "../EditPanelBuilds";
import cx from "classnames";
import styles from "./MutatorView.module.scss";

const MutatorViewImplementation = () => {
    const client = useApolloClient();
    const location = useLocation();
    const { productId } = useParams<{
        productId: string;
    }>();
    const { revisionId } = useParams<{
        revisionId: string;
    }>();
    const revision = useRevision(Number(revisionId));
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const { developerMode } = useLocalState();
    const mutatorState = useMutatorState();
    const [showBuildOverview, setShowBuildOverview] = useState(false);
    const [mutatorWorkingCopy, updateMutatorWorkingCopy] = useState<
        Mutator | undefined
    >(loadMutatorConfigLocal(revisionId));
    const [lastUpdatedTimeStamp, updateLastUpdatedTimestamp] = useState(
        Date.now()
    );
    const [updating, setUpdating] = useState(false);
    const [alertInfo, setAlertInfo] = useState({ message: "", variant: "" });
    const [showMutatorView, updateShowMutatorView] = useState(true);
    const [portrait, setPortrait] = useState(true);
    const [hasUnsaved, updateHasUnsaved] = useState(false);
    const [iFrameContent, setIFrameContent] = useState<string | undefined>(
        undefined
    );
    const [isBuilding, setIsBuilding] = useState(false);
    const [buildId, setBuildId] = useState(0);
    const { data: { revisionConfig } = {} } = useQuery<
        RevisionConfigData,
        RevisionVariables
    >(GET_REVISION_CONFIG, {
        variables: { revisionId: Number(revision.id) }
    });
    const { loading, data: { mutatorConfig } = {} } = useQuery<
        MutatorQueryData,
        MutatorVariables
    >(MUTATOR_QUERY, {
        variables: {
            revisionId: Number(revisionId)
        }
    });
    const { data: { mutatorAssetbundles } = {} } = useQuery<
        MutatorAssetbundlesQueryData,
        MutatorVariables
    >(MUTATOR_ASSETBUNDLES, {
        variables: {
            revisionId: Number(revisionId)
        }
    });

    const [createMutatorConfig] = useMutation<
        MutatorCreateData,
        MutatorVariables
    >(MUTATOR_CREATE_CONFIG, {
        variables: {
            revisionId: Number(revisionId)
        },
        refetchQueries: [
            {
                query: MUTATOR_QUERY,
                variables: { revisionId: Number(revisionId) }
            }
        ]
    });
    const [updateMutatorMutation] = useMutation<
        MutatorUpdateData,
        MutatorUpdateVariables
    >(MUTATOR_UPDATE_CONFIG);

    const [createBuild] = useMutation<
        CreateBatchBuildData,
        CreateBatchBuildVariables
    >(CREATE_BUILD);
    const [cancelBuild] = useMutation<
        CancelBatchBuildData,
        CancelBatchBuildVariables
    >(CANCEL_BUILD);

    // @ts-ignore
    const [getTempLink, { data: { generateLink } = {} }] = useLazyQuery<
        TempLink,
        TempLinkVariables
    >(GET_TEMP_LINK, { fetchPolicy: "network-only" });

    const [getExampleCommand, { data }] = useLazyQuery<
        ExampleCommandData,
        TempBuildVariables
    >(GET_EXAMPLE_COMMAND, { fetchPolicy: "network-only" });

    useSubscription(MUTATOR_BUILD_SUBSCRIPTION, {
        variables: { revisionId: revisionId },
        onData: ({
            data: { data: { mutatorBuildNotification } = {} },
            client
        }) => {
            setIsBuilding(false);
            if (
                mutatorBuildNotification &&
                mutatorBuildNotification.mutatorAssetbundles
            ) {
                client.cache.writeQuery<{
                    mutatorAssetbundles: MutatorAssetbundlesQueryData;
                }>({
                    query: MUTATOR_ASSETBUNDLES,
                    variables: { revisionId: Number(revisionId) },
                    data: {
                        mutatorAssetbundles:
                            mutatorBuildNotification.mutatorAssetbundles
                    }
                });
            }
            if (
                generateLink &&
                generateLink.link &&
                mutatorBuildNotification &&
                mutatorBuildNotification.mutatorAssetbundles
            ) {
                console.log(
                    "[DEBUG] MUTATOR_BUILD_SUBSCRIPTION->updateMutatorHtml"
                );
                updateMutatorHtml(
                    generateLink.link,
                    mutatorBuildNotification.mutatorAssetbundles
                );
            }
        }
    });

    useSubscription(MUTATOR_CONFIG_SUBSCRIPTION, {
        variables: { revisionId: revisionId },
        onData: ({
            data: { data: { mutatorConfigNotification } = {} },
            client
        }) => {
            const currentCache = client.cache.readQuery<
                MutatorQueryData,
                MutatorVariables
            >({
                query: MUTATOR_QUERY,
                variables: {
                    revisionId: Number(revisionId)
                }
            });
            const currentConfig = currentCache?.mutatorConfig;
            if (currentConfig && data) {
                const version = currentConfig.mutatorConfig.version;
                const newConfig = mutatorConfigNotification.mutatorConfig;
                checkBuildDataVersion(newConfig.version, version);
                client.cache.writeQuery<MutatorQueryData, MutatorVariables>({
                    query: MUTATOR_QUERY,
                    data: {
                        mutatorConfig: {
                            ...currentConfig,
                            mutatorConfig: newConfig
                        }
                    },
                    variables: {
                        revisionId: Number(revisionId)
                    }
                });
            }
        }
    });

    const updateMutatorHtml = useCallback(
        async (
            url: string,
            newAssetbundles: AssetBundle[] | undefined = undefined
        ) => {
            const assetbundles =
                newAssetbundles !== undefined
                    ? newAssetbundles
                    : mutatorAssetbundles;
            const finalHtmlText = await fetchHtmlFromS3(
                url,
                mutatorWorkingCopy,
                assetbundles
            );
            setIFrameContent(finalHtmlText);
        },
        [mutatorWorkingCopy, mutatorAssetbundles, setIFrameContent]
    );

    useEffect(() => {
        getExampleCommand({
            variables: {
                revisionId: Number(revisionId),
                targetNetwork: "mutator"
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mutatorConfig && mutatorConfig.mutatorKeyPrefix) {
            getTempLink({
                variables: {
                    key: mutatorConfig.mutatorKeyPrefix + "/index.html",
                    productId: Number(productId)
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mutatorConfig?.mutatorKeyPrefix]);

    useEffect(() => {
        if (
            generateLink?.link &&
            mutatorWorkingCopy &&
            mutatorAssetbundles !== undefined
        ) {
            console.log(
                "[DEBUG] useEffect->mutatorWorkingCopy->updateMutatorHtml"
            );
            updateMutatorHtml(generateLink.link);
            saveMutatorConfigLocal(revisionId, mutatorWorkingCopy);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mutatorWorkingCopy]);

    useEffect(() => {
        const updateMutatorConfig = async () => {
            updateMutatorWorkingCopy(mutatorConfig?.mutatorConfig);
        };
        if (
            generateLink?.link &&
            mutatorConfig?.mutatorConfig &&
            mutatorAssetbundles !== undefined
        ) {
            if (
                mutatorWorkingCopy?.version ===
                mutatorConfig.mutatorConfig.version
            ) {
                if (iFrameContent === undefined) {
                    console.log(
                        "[DEBUG] useEffect->1st time render->updateMutatorHtml"
                    );
                    updateMutatorHtml(generateLink.link);
                }
                return;
            }
            updateMutatorConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateLink?.link, mutatorConfig?.mutatorConfig, mutatorAssetbundles]);

    useEffect(() => {
        if (updating) {
            setAlertInfo({
                message: "Updating changes...",
                variant: "warning"
            });
        } else if (alertInfo.message === "Updating changes...") {
            setAlertInfo({
                message: "Configuration updated successfully",
                variant: "success"
            });
            updateHasUnsaved(false);
            const timer = setTimeout(
                () => setAlertInfo({ message: "", variant: "" }),
                3000
            );
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updating]);

    const onChange = (name: string, value: any) => {
        if (!mutatorWorkingCopy) {
            return;
        }
        const newMutator: Mutator = {
            ...mutatorWorkingCopy,
            properties: mutatorWorkingCopy.properties.map(property => {
                if (property.name === name) {
                    return { ...property, value };
                } else {
                    return { ...property };
                }
            })
        };
        updateJson(newMutator);
    };

    const updateJson = (newMutatorData: Mutator, save = false) => {
        if (save) {
            saveMutatorConfig(newMutatorData);
        } else {
            updateLastUpdatedTimestamp(Date.now());
            updateMutatorWorkingCopy(newMutatorData);
        }
    };

    const saveMutatorConfig = async (newMutatorData?: Mutator) => {
        const newData = newMutatorData ? newMutatorData : mutatorWorkingCopy;
        if (newData === undefined) {
            return;
        }
        setUpdating(true);
        const input: MutatorUpdateInput = {
            revisionId: Number(revisionId),
            mutatorConfig: newData
        };
        updateMutatorMutation({
            variables: {
                input: input
            },
            update: (cache, { data }) => {
                const currentCache = cache.readQuery<
                    MutatorQueryData,
                    MutatorVariables
                >({
                    query: MUTATOR_QUERY,
                    variables: {
                        revisionId: Number(revisionId)
                    }
                });
                const currentConfig = currentCache?.mutatorConfig;
                if (currentConfig && data) {
                    const version = currentConfig.mutatorConfig.version;
                    const newConfig = data.mutatorUpdateConfig;
                    checkBuildDataVersion(
                        newConfig.mutatorConfig.version,
                        version
                    );
                    cache.writeQuery<MutatorQueryData, MutatorVariables>({
                        query: MUTATOR_QUERY,
                        data: {
                            mutatorConfig: newConfig
                        },
                        variables: {
                            revisionId: Number(revisionId)
                        }
                    });
                    updateLastUpdatedTimestamp(Date.now());
                    updateMutatorWorkingCopy(newConfig.mutatorConfig);
                }
                setUpdating(false);
                updateHasUnsaved(false);
            }
        }).catch(error => {
            console.log(
                "[DEBUG] updateMutatorMutation failed with error ",
                error
            );
            // @ts-ignore
            if (
                error.message === "Build data out of sync" ||
                error.message ===
                    "resolve_mutator_update_config failed with error: out of date version in mutator config"
            ) {
                client
                    .refetchQueries({
                        include: ["mutatorConfig"]
                    })
                    .then(result => {
                        if (result.length > 0) {
                            updateNotificationState(client, false, true, {
                                success: false,
                                header: "Data out of sync",
                                message: "Data has been updated",
                                delay: 3000
                            });
                        }
                    })
                    .catch(error => {
                        console.log(
                            "[DEBUG] refetch failed with error ",
                            error
                        );
                    })
                    .finally(() => {
                        updateLastUpdatedTimestamp(Date.now());
                        setUpdating(false);
                    });
            } else {
                setUpdating(false);
            }
        });
    };

    const build = async () => {
        if (!data?.getExampleCommand) {
            return;
        }

        const variables: CreateBatchBuildVariables = {
            revisionId: Number(revisionId),
            buildData: JSON.stringify([data?.getExampleCommand]),
            buildType: BuildType.Mutator
        };

        try {
            const response = await createBuild({
                variables: variables,
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: {
                            revisionId: revisionId,
                            clientTest: !developerMode
                        }
                    }
                ]
            });
            updateNotificationState(client, false, true, {
                success: true,
                header: "Build Status",
                message: `Build started successfully! (id: ${response?.data?.createBuild.id})`,
                delay: 3000
            });
            setBuildId(Number(response?.data?.createBuild.id));
            setIsBuilding(true);
        } catch (error) {
            console.log("[DEBUG] build error ", error);
            updateNotificationState(client, false, true, {
                success: false,
                header: "Build Status",
                // @ts-ignore
                message: error.message,
                delay: 3000
            });
        }
    };

    const startCancelJob = async () => {
        try {
            await cancelBuild({
                variables: { buildId: buildId },
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: {
                            revisionId: revisionId,
                            clientTest: developerMode
                        }
                    }
                ]
            });
            setIsBuilding(false);
        } catch (error) {
            console.log("[DEBUG] startCancelJob error ", error);
        }
    };

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasUnsaved && currentLocation.pathname !== nextLocation.pathname
    );

    if (loading && !mutatorWorkingCopy) {
        return (
            <>
                <Row className="p-2 d-flex fullSize">
                    <Col className="col-3 pt-2 ps-2 pb-2">
                        <BackButton
                            hasHistory={
                                !(location.key && navigationType === "POP")
                            }
                            goBack={() => navigate(-1)}
                            size={"lg"}
                            title="Back to Previous page"
                        />
                    </Col>
                </Row>
                <div className="aditorViewSmall text-contrast d-flex justify-content-center align-items-center">
                    <div>
                        <h5 className="text-center text-subtle">Loading...</h5>
                    </div>
                </div>
            </>
        );
    }

    if (!loading && !mutatorWorkingCopy) {
        if (!developerMode) {
            return (
                <>
                    <Row className="p-2 text-contrast d-flex fullSize">
                        <Col className="col-3 pt-2 ps-2 pb-2">
                            <BackButton
                                hasHistory={
                                    !(location.key && navigationType === "POP")
                                }
                                goBack={() => navigate(-1)}
                                size={"lg"}
                                title="Back to Previous page"
                            />
                        </Col>
                    </Row>
                    <div className="aditorViewSmall text-contrast d-flex justify-content-center align-items-center">
                        <div>
                            <h5 className="text-center">
                                You should not be seeing this
                            </h5>
                            <br />
                            <span>
                                The developer has enabled customator, but there
                                is no configuration yet. Check back later.
                            </span>
                        </div>
                    </div>
                </>
            );
        }
        return (
            <>
                <Row className="p-2 text-contrast d-flex fullSize">
                    <Col className="col-3 pt-2 ps-2 pb-2">
                        <BackButton
                            hasHistory={
                                !(location.key && navigationType === "POP")
                            }
                            goBack={() => navigate(-1)}
                            size={"lg"}
                            title="Back to Previous page"
                        />
                    </Col>
                </Row>
                <div className="aditorViewSmall text-contrast d-flex justify-content-center align-items-center">
                    <Button onClick={() => createMutatorConfig()}>
                        Start By Creating a New Mutator Config
                    </Button>
                </div>
            </>
        );
    }

    const historyPath = location.pathname.replace(
        "variations/customator",
        "history"
    );

    return (
        <div
            className={`${styles.mutatorLayout} ${
                developerMode ? "pageMaxWidthDev" : "pageMaxWidthClient"
            }`}
        >
            <Row className="p-1 mb-2 text-contrast d-flex">
                <Col xs={"auto"} className="p-0 m-0">
                    {developerMode ? (
                        <div className="p-3">
                            <BackButton
                                hasHistory={
                                    !(location.key && navigationType === "POP")
                                }
                                goBack={() => navigate(-1)}
                                size={"lg"}
                                title="Back to Previous page"
                            />
                        </div>
                    ) : (
                        <div
                            onClick={() =>
                                location.key && navigationType !== "POP"
                                    ? navigate(-1)
                                    : navigate("/products")
                            }
                            className={`mouseHover me-4 ${styles.clientBackButton}`}
                        >
                            <FontAwesomeIcon
                                icon={faChevronCircleLeft}
                                size="lg"
                                className="text-subtle"
                            />
                        </div>
                    )}
                </Col>
                <Col xs={"auto"}>
                    {!developerMode && alertInfo.message ? (
                        <div>
                            <Alert
                                className={styles.alertModal}
                                variant={alertInfo.variant}
                            >
                                <span>{alertInfo.message}</span>
                            </Alert>
                        </div>
                    ) : null}
                </Col>
                <Col
                    xs={"auto"}
                    className="d-flex mb-2 align-items-center justify-content-end ms-auto"
                >
                    {developerMode ? (
                        <>
                            <Form.Group className="d-flex align-items-center mt-4">
                                <Form.Label
                                    className={
                                        showMutatorView
                                            ? "fw-bold text-primary"
                                            : ""
                                    }
                                >
                                    Mutator
                                </Form.Label>
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 550, hide: 100 }}
                                    overlay={
                                        <Tooltip id="mutator-view-toggle-tooltip">
                                            Toggle between mutator view and JSON
                                            editor
                                        </Tooltip>
                                    }
                                >
                                    <Form.Check
                                        type="switch"
                                        id="mutator-view-toggle"
                                        checked={!showMutatorView}
                                        onChange={e =>
                                            updateShowMutatorView(
                                                !e.target.checked
                                            )
                                        }
                                        className="ms-2 mb-2"
                                    />
                                </OverlayTrigger>
                                <Form.Label
                                    className={
                                        !showMutatorView
                                            ? "fw-bold text-primary"
                                            : ""
                                    }
                                >
                                    JSON
                                </Form.Label>
                            </Form.Group>
                            {isBuilding ? (
                                <Button
                                    onClick={() => {
                                        startCancelJob();
                                    }}
                                    className={cx(
                                        styles.createBuild,
                                        "btn-danger ms-3"
                                    )}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    className={cx(
                                        styles.infoButton,
                                        "btn-success ms-3",
                                        {
                                            disabled: isBuilding
                                        }
                                    )}
                                    onClick={() => {
                                        build();
                                    }}
                                >
                                    Create Mutator Build
                                </Button>
                            )}
                        </>
                    ) : (
                        <div className="d-flex">
                            <EditPanelBuilds
                                revision={revision}
                                buttonOnly={true}
                            />
                            {/* OVERVIEW BUTTON HIDDEN UNTIL FURTHER REQUEST */}
                            {/* <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="build-history-tooltip">
                                        Show info about builds that will be
                                        generated (pop up)
                                    </Tooltip>
                                }
                            >
                                <Button
                                    variant="info"
                                    className={styles.infoButton}
                                    disabled={
                                        !revisionConfig?.buildJobCount ||
                                        revisionConfig.buildJobCount === 0
                                    }
                                    onClick={() => setShowBuildOverview(true)}
                                >
                                    <span className={styles.infoButtonText}>
                                        Show Build Overview
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faList}
                                        className={styles.infoButtonIcon}
                                    />
                                </Button>
                            </OverlayTrigger> */}
                            <Link to={historyPath}>
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 550, hide: 100 }}
                                    overlay={
                                        <Tooltip id="build-history-tooltip">
                                            Go to list of all generated builds
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        className={cx(
                                            styles.forceButtonWide,
                                            "mx-2"
                                        )}
                                    >
                                        Build History
                                    </Button>
                                </OverlayTrigger>
                            </Link>
                        </div>
                    )}
                </Col>
            </Row>
            {showMutatorView ? (
                <>
                    <div
                        className={cx(styles.mutatorView, {
                            [styles.portraitLayout]: portrait,
                            [styles.landscapeLayout]: !portrait
                        })}
                    >
                        <div
                            className={cx(
                                styles.mutatorPreview,
                                "text-muted bread-text"
                            )}
                        >
                            <Form.Group className="d-flex justify-content-center gap-3">
                                <Form.Label
                                    role="button"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setPortrait(true)}
                                    className={
                                        portrait
                                            ? "fw-bold text-purple mb-0"
                                            : "text-subtle mb-0"
                                    }
                                >
                                    Portrait
                                </Form.Label>
                                <Form.Label
                                    role="button"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setPortrait(false)}
                                    className={
                                        !portrait
                                            ? "fw-bold text-purple mb-0"
                                            : "text-subtle mb-0"
                                    }
                                >
                                    Landscape
                                </Form.Label>
                            </Form.Group>
                            <div
                                className={cx(styles.previewFrame, {
                                    [styles.previewFramePortrait]: portrait,
                                    [styles.previewFrameLandscape]: !portrait
                                })}
                            >
                                <iframe
                                    className={styles.previewIframe}
                                    title="Customator"
                                    srcDoc={iFrameContent}
                                ></iframe>
                            </div>
                        </div>
                        <div className={styles.mutatorOptions}>
                            <Row className="d-flex justify-content-between align-items-center">
                                {mutatorState.showMeta ? (
                                    <Col
                                        xs={"auto"}
                                        className="text-subtle bread-text"
                                    >
                                        Try out different settings in the option
                                        cards below!
                                    </Col>
                                ) : null}
                                <Col
                                    xs={"auto"}
                                    className={cx(
                                        styles.showDescriptions,
                                        "ms-auto"
                                    )}
                                >
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 550, hide: 100 }}
                                        overlay={
                                            <Tooltip id="toggle-details-tooltip">
                                                Toggle help and additional info
                                                on all options
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            onClick={() =>
                                                updateMutatorState(
                                                    client,
                                                    !mutatorState.showMeta
                                                )
                                            }
                                            className={cx(
                                                styles.infoIcon,
                                                "text-subtle"
                                            )}
                                        >
                                            <span
                                                className={cx(
                                                    "mx-2 bread-text",
                                                    styles.infoToggleText
                                                )}
                                            >
                                                {!mutatorState.showMeta
                                                    ? "Show descriptions"
                                                    : "Hide descriptions"}
                                            </span>
                                            <FontAwesomeIcon
                                                icon={faCircleInfo}
                                            />
                                        </div>
                                    </OverlayTrigger>
                                </Col>
                            </Row>
                            <MutatorOptions
                                mutatorConfig={mutatorWorkingCopy}
                                assetBundles={mutatorAssetbundles || []}
                                onChange={onChange}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className={styles.jsonView}>
                    <EditableJsonView
                        key={lastUpdatedTimeStamp}
                        jsonData={JSON.stringify(mutatorWorkingCopy, null, 4)}
                        updateJsonData={newData => updateJson(newData, true)}
                        updating={updating}
                        updateHasUnsavedData={updateHasUnsaved}
                    />
                    {hasUnsaved && (
                        <div className="text-muted text-center bread-text">
                            Unsaved changes!
                        </div>
                    )}
                </div>
            )}
            <SimpleToast />
            {showBuildOverview && revision && (
                <BuildOverviewModal
                    revision={revision}
                    onClose={() => setShowBuildOverview(false)}
                />
            )}
            {blocker.state === "blocked" ? (
                <AlertModal
                    header={<h5>Are you sure you want to leave?</h5>}
                    component="You have unsaved changes."
                    visible={() => blocker.reset()}
                    footer={
                        <Button
                            variant="warning"
                            className="m-2"
                            onClick={() => blocker.proceed()}
                        >
                            Leave
                        </Button>
                    }
                />
            ) : null}
        </div>
    );
};

export const MutatorView = React.memo(MutatorViewImplementation);
